import { useEffect } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { SiteNav } from "./SiteNav";
import Footer from "./Footer";
import LanguageSuggestionBanner from "./LanguageSuggestionBanner";
import { MessageCircle } from "lucide-react";
import { useLocale } from "../i18n/useLocale";
import { LocaleContext } from "../i18n/LocaleContext";
import { liveLocale } from "../i18n/locales";
import { GlobalCursor } from "../ds/core";
import { getLenis } from "../ds/core/lenisBus";
import { markHydrated } from "../ds/core/motion";

/**
 * Contact widget — a WhatsApp launcher (DIT-264 / S8). The design app shipped a
 * canned-decoy fake chat (hardcoded Q&A) which we deliberately dropped: the
 * client's support channel is WhatsApp and there is no live-chat backend. The
 * floating button opens wa.me in a new tab, prefilled with a localized intro.
 *
 * The number is build-time configurable via `VITE_WHATSAPP_NUMBER` (digits only,
 * incl. country code, no `+`). Absent → the button hides rather than linking to a
 * dead number.
 */
function ContactWidget() {
  const { t } = useLocale();
  const number = (import.meta.env.VITE_WHATSAPP_NUMBER ?? "").replace(/[^\d]/g, "");
  if (!number) return null;

  const href = `https://wa.me/${number}?text=${encodeURIComponent(t("common:chat.greeting"))}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("common:chat.openAria")}
      className="fixed bottom-4 right-4 z-50 flex items-center justify-center rounded-full bg-[#25D366] shadow-lg transition-transform hover:scale-105 active:scale-95 md:right-6"
      style={{ width: 52, height: 52 }}
    >
      <MessageCircle size={22} strokeWidth={2} className="text-white" />
    </a>
  );
}

export default function Layout() {
  const { pathname, hash } = useLocation();

  // The browser's own scroll restoration ("auto") re-applies the previous
  // position after a client-side navigation, overwriting our reset — so a link
  // like "All questions" opened the next page mid-way down. We own the scroll
  // here, so tell the browser to keep out.
  // From here on, anything that mounts is a later mount and may play its
  // entrance; everything in the first pass matched the prerender (DIT-553).
  useEffect(() => { markHydrated(); }, []);

  useEffect(() => {
    if (!("scrollRestoration" in history)) return;
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => { history.scrollRestoration = previous; };
  }, []);

  useEffect(() => {
    // Honour an in-page anchor (e.g. /features#vehicles from the mega-menu);
    // otherwise reset to the top on a route change. A rAF lets the new route's
    // DOM commit before we look the element up.
    //
    // Reset THROUGH Lenis when smooth scrolling is on: Lenis owns the scroll
    // position and re-applies its own on the next frame, so a bare
    // window.scrollTo(0,0) gets overwritten and the new page opens mid-way
    // down (what the client saw clicking "Alle vragen").
    const toTop = () => {
      const lenis = getLenis();
      if (lenis) {
        // Lenis still holds the previous page's dimensions on the frame the
        // route swaps, so it clamps the jump against the old height — resize
        // first, then jump, and re-assert once more after layout settles.
        lenis.resize();
        lenis.scrollTo(0, { immediate: true, force: true });
      }
      // `behavior: "instant"` overrides the global `scroll-behavior: smooth`.
      // Without it the reset ANIMATES: the new route opens still showing the
      // previous page's offset and slides up over ~1.2s — the same "opens
      // mid-way down" the client reported, just arriving a different way.
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    };
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const jump = () => {
        const el = document.getElementById(id);
        if (el) {
          const lenis = getLenis();
          if (lenis) lenis.scrollTo(el, { immediate: true });
          else el.scrollIntoView({ block: "start" });
        } else toTop();
      };
      const raf = requestAnimationFrame(jump);
      return () => cancelAnimationFrame(raf);
    }
    toTop();
    const raf = requestAnimationFrame(toTop);
    const settle = window.setTimeout(toTop, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); };
  }, [pathname, hash]);

  // Home is overlaid with the Framer design: it renders its own DS-core Header
  // inside the hero, so the shared Navbar is suppressed there. Under prefix-all
  // the home path is /nl or /en — detect the locale index by segment count.
  const ownNav = pathname.split("/").filter(Boolean).length <= 1;

  // The shared shell (SiteNav + Footer) renders ABOVE the `:lang` LocaleLayout
  // provider, so resolve the active locale here too (useParams() returns params
  // from the whole matched tree) and provide it — otherwise shell links drop the
  // locale prefix. Outside `:lang` (no param) → null → links pass through.
  const activeLocale = liveLocale(useParams().lang) ?? null;

  // NOTE: `pref_locale` is deliberately NOT written here. It is the edge
  // geo-redirect's manual-override cookie (S10), and writing it on every visited
  // locale pinned the very first landing forever: a visitor geo-routed to /en got
  // `pref_locale=en` for a year, so the cookie then beat geo on every later visit
  // — moving to Belgium (or testing over a BE VPN) could never reach /nl again
  // (DIT-264 QA). It is now written only by an explicit switch, in
  // `useSwitchLocale` (i18n/useSwitchLocale.ts).

  return (
    <LocaleContext.Provider value={activeLocale}>
      {/* Lenis smooth scroll is OFF. It drove the page from JS every frame,
          forcing a scroll-linked recalc + paint over the whole document: the
          home page measured p95 93.8ms with 17 dropped frames, vs 9.3ms and
          none with Lenis disabled (production build, only variable changed).
          Anchor jumps stay smooth via native `scroll-behavior: smooth`
          (index.css); every getLenis() caller already falls back to native
          scrolling when it returns null. */}
      <div className="flex min-h-screen flex-col bg-bg">
        {!ownNav && <SiteNav />}
        {/* Page content rounds into the footer consistently: a 40px rounded bottom
            overlapping the dark footer by 40px. overflow-clip, not -hidden, so it
            doesn't break the Workflow sticky stack. */}
        <main className="relative z-10 -mb-10 flex-1 overflow-clip rounded-b-[40px]">
          <Outlet />
        </main>
        <Footer />
        <ContactWidget />
        <LanguageSuggestionBanner />
      </div>
      <GlobalCursor />
    </LocaleContext.Provider>
  );
}
